import requestApi from '../../../utils/Request';
import { showErrorAlert } from '../../../utils/AlertUtils';
import {
  requestEInvoiceList,
  responseEInvoiceList,
  failureEInvoiceList,
  requestEInvoiceView,
  responseEInvoiceView,
  failureEInvoiceView,
  requestEInvoiceDownload,
  responseEInvoiceDownload,
  failureEInvoiceDownload,
  requestCorrectRecipients,
  responseCorrectRecipients,
  failureCorrectRecipients,
  requestCreditInvoice,
  responseCreditInvoice,
  failureCreditInvoice,
  requestSPList,
  responseSPList,
  failureSPList,
  requestSPDownload,
  responseSPDownload,
  failureSPDownload,
} from '../../slices/eInvoiceSlice/eInvoiceSlice';
import {
  EINVOICE_MERCHANT,
  EINVOICE_CUSTOMER,
  EINVOICE_SALES_PARTNER_LIST,
  EINVOICE_SALES_PARTNER_PDF,
} from 'utils/StaticApiEndpoints';
import Swal from 'sweetalert2';

///////// List E-Invoice Data //////////
export const ListEInvoiceData = (payload = {}) => {
  return (dispatch) => {
    const {
      page = 1,
      limit = 10,
      region = '',
      invoiceId = '',
      transactionId = '',
      merchantName = '',
      invoiceType = 'B2C', // B2C or B2B
      start = null,
      end = null,
    } = payload;

    dispatch(requestEInvoiceList());

    const params = new URLSearchParams({
      page: page.toString(),
      limit: limit.toString(),
    });

    if (region) params.append('region', region);
    if (invoiceId) params.append('invoiceId', invoiceId);
    if (transactionId) params.append('transactionId', transactionId);
    if (merchantName) params.append('merchantName', merchantName);
    if (start) params.append('start', start);
    if (end) params.append('end', end);

    // Choose endpoint based on invoice type
    const endpoint =
      invoiceType === 'B2B' ? EINVOICE_MERCHANT : EINVOICE_CUSTOMER;

    // API integration:
    requestApi
      .get(`${endpoint}?${params.toString()}`)
      .then((response) => {
        // Handle the response structure: { success: true, data: { list: [], total: 0, ... } }
        dispatch(responseEInvoiceList(response));
      })
      .catch((error) => {
        dispatch(failureEInvoiceList(error));
        showErrorAlert(error?.message || 'Failed to load e-invoices');
      });
  };
};

///////// View E-Invoice Details //////////
export const ViewEInvoiceData = (payload) => {
  return (dispatch) => {
    const { id, invoiceType = 'B2B' } = payload;

    dispatch(requestEInvoiceView());

    // Choose endpoint based on invoice type
    const endpoint =
      invoiceType === 'B2B' ? EINVOICE_MERCHANT : EINVOICE_CUSTOMER;

    requestApi
      .get(`${endpoint}/${id}`)
      .then((response) => {
        dispatch(responseEInvoiceView(response?.data));
      })
      .catch((error) => {
        dispatch(failureEInvoiceView(error));
        showErrorAlert(error?.message || 'Failed to load invoice details');
      });
  };
};

///////// Download E-Invoice PDF //////////
export const DownloadEInvoicePDF = (payload, callback) => async (dispatch) => {
  try {
    dispatch(requestEInvoiceDownload());

    const { id, invoiceType = 'B2B', invoiceId } = payload;

    // Choose endpoint based on invoice type
    const endpoint =
      invoiceType === 'B2B' ? EINVOICE_MERCHANT : EINVOICE_CUSTOMER;

    const response = await requestApi.get(`${endpoint}/${id}/download`, {
      responseType: 'blob',
    });

    const blob = new Blob([response], { type: 'application/pdf' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');

    a.href = url;
    const fileName = invoiceId || id;
    a.download = `${fileName}.pdf`;

    a.click();
    window.URL.revokeObjectURL(url);
    dispatch(responseEInvoiceDownload());
    if (typeof callback === 'function') {
      callback();
    }
  } catch (error) {
    Swal.fire({
      icon: 'error',
      text: error?.message,
    });
    dispatch(failureEInvoiceDownload(error?.message));
  }
};

///////// List Sales Partner Invoice Data //////////
export const ListSPInvoiceData = (payload = {}) => {
  return (dispatch) => {
    const { page = 1, limit = 10 } = payload;

    dispatch(requestSPList());

    const params = new URLSearchParams({
      page: page.toString(),
      limit: limit.toString(),
    });

    requestApi
      .get(`${EINVOICE_SALES_PARTNER_LIST}?${params.toString()}`)
      .then((response) => {
        dispatch(responseSPList(response?.data));
      })
      .catch((error) => {
        dispatch(failureSPList(error));
        showErrorAlert(
          error?.message || 'Failed to load sales partner invoices',
        );
      });
  };
};

///////// Download Sales Partner Invoice PDF //////////
export const DownloadSPInvoicePDF = (payload, callback) => async (dispatch) => {
  try {
    dispatch(requestSPDownload());

    const { id, invoiceId, sellerName, year, quarter, month } = payload;

    const response = await requestApi.get(
      `${EINVOICE_SALES_PARTNER_PDF}/${id}/pdf`,
      { responseType: 'blob' },
    );

    const blob = new Blob([response], { type: 'application/pdf' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');

    a.href = url;
    const period = quarter
      ? `Q${quarter}`
      : `M${String(month).padStart(2, '0')}`;
    a.download = invoiceId
      ? `${invoiceId}.pdf`
      : `${sellerName}-${period}-${year}.pdf`;
    a.click();
    window.URL.revokeObjectURL(url);

    dispatch(responseSPDownload());
    if (typeof callback === 'function') {
      callback();
    }
  } catch (error) {
    Swal.fire({ icon: 'error', text: error?.message });
    dispatch(failureSPDownload(error?.message));
  }
};

export const CorrectRecipients = (payload) => {
  return (dispatch) => {
    const { id, data, callback } = payload;
    dispatch(requestCorrectRecipients());
    requestApi
      .post(`${EINVOICE_MERCHANT}/${id}/correct-recipients`, data)
      .then(() => {
        dispatch(responseCorrectRecipients());
        Swal.fire({
          icon: 'success',
          text: 'Recipients corrected successfully',
        });
        if (callback) callback();
      })
      .catch((error) => {
        dispatch(failureCorrectRecipients(error));
        showErrorAlert(error?.message || 'Failed to correct recipients');
      });
  };
};

///////// Credit Invoice ////////
export const CreditInvoice = (payload) => {
  return (dispatch) => {
    const { id, callback, errorCallback } = payload;
    dispatch(requestCreditInvoice());
    requestApi
      .post(`${EINVOICE_MERCHANT}/${id}/credit`, {})
      .then((response) => {
        dispatch(responseCreditInvoice());
        Swal.fire({
          icon: 'success',
          text: response?.message || 'Credit invoice created successfully',
        });
        if (callback) callback();
      })
      .catch((error) => {
        dispatch(failureCreditInvoice(error));
        showErrorAlert(error?.message || 'Failed to create credit invoice');
        if (errorCallback) errorCallback();
      });
  };
};
